import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/24/outline';
import {
  FilterIcon,
  Button,
  Feedback,
  SkeletonLoader,
  useSlideOver,
} from '@plerionhq/design-system';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useListGrants, fetchAllGrants } from '../../api/use-list-grants';
import { useGrantsScope } from '../../api/use-grants-scope';
import { useListResourceTypes } from '../../api/use-list-resource-types';
import {
  FilterTooLongError,
  MAX_QUERY_STRING_BYTES,
} from '../../lib/grants-query';
import type { AccessGrant } from '../../types';
import { AccessGrantSlideOverContent } from './access-grant-slide-over-content';
import { AccessGrantsFilters } from './access-grants-filters';
import { AccessGrantsTable } from './access-grants-table';
import { GrantsKpis } from './grants-kpis';
import { PresetChips } from './preset-chips';
import { useGrantsView } from './use-grants-view';
import {
  grantsToCsv,
  downloadCsv,
  buildExportFilename,
} from '../../lib/grants-to-csv';

import { useTranslation } from 'react-i18next';

function PageHeader({
  isExporting,
  onExport,
}: {
  isExporting: boolean;
  onExport: () => void;
}) {
  const { t } = useTranslation('accessGrants');
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-2xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        {t('resourceAccessGrants')}
      </h1>
      <Button
        variant="secondary"
        size="small"
        label={t('exportCsv')}
        icon={<ArrowDownTrayIcon className="h-4 w-4" />}
        onClick={onExport}
        isLoading={isExporting}
      />
    </div>
  );
}

// Shown instead of the generic error state when the filter is too long for the
// edge to accept. Says which filter to trim, because Resource type is the only
// one that can realistically get there.
function FilterTooLongNotice({
  queryStringBytes,
}: {
  queryStringBytes: number;
}) {
  const { t } = useTranslation('accessGrants');
  return (
    <Feedback
      variant="warning"
      title={t('thisFilterIsTooLongToSend')}
      description={t('filterQueryTooLongDescription', {
        used: queryStringBytes.toLocaleString(),
        max: MAX_QUERY_STRING_BYTES.toLocaleString(),
      })}
    />
  );
}

function useAccessGrantSlideOver() {
  const { slideOver } = useSlideOver();

  return useCallback(
    (grant: AccessGrant) => {
      const slideOverId = `grant-details-${crypto.randomUUID()}`;
      slideOver({
        mfaId: 'access-grants-app',
        id: slideOverId,
        overlay: true,
        slideOverClassName: `${slideOverId} h-full max-w-[70vw] bg-gray-50 dark:bg-gray-900 rounded-tl-3xl overflow-hidden`,
        children: (
          <>
            <style>{`.${slideOverId} .slide-over-close { display: none !important; }`}</style>
            <AccessGrantSlideOverContent
              grant={grant}
              slideOverId={slideOverId}
            />
          </>
        ),
      });
    },
    [slideOver],
  );
}

export function InventoryPage() {
  const { t } = useTranslation('accessGrants');
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const {
    data,
    status,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListGrants();
  const { data: allResourceTypes } = useListResourceTypes();
  const { search } = useLocation();
  const openSlideOver = useAccessGrantSlideOver();
  const grantsView = useGrantsView();

  // Scroll-container ref for the table's infinite-scroll IntersectionObserver.
  // The page itself doesn't scroll (h-full + flex column); the inner div below
  // is the actual scroll parent. Using the viewport as the observer root —
  // the IntersectionObserver default — never reliably reports the sentinel as
  // intersecting because the parent's overflow clips before the viewport
  // edge, so onLoadMore never fires.
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const toggleExpandFilter = useCallback(
    () => setIsFilterExpanded((v) => !v),
    [],
  );

  const grants = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );
  // Total from page 0 only — may drift if rows are added during pagination.
  const total = data?.pages[0]?.meta.total ?? 0;

  const handleLoadMore = useCallback(
    () => void fetchNextPage(),
    [fetchNextPage],
  );

  const scope = useGrantsScope();
  const handleExport = useCallback(() => {
    const filename = buildExportFilename(search);
    const toastId = toast.loading(t('downloadingGrantsCsv'));
    setIsExporting(true);
    void fetchAllGrants(search, allResourceTypes, undefined, scope)
      .then((allGrants) => {
        toast.dismiss(toastId);
        if (allGrants.length === 0) {
          toast.error(t('noGrantsFoundMatchingTheCurrentFilters'));
          return;
        }
        downloadCsv(grantsToCsv(allGrants), filename);
      })
      .catch((exportError: unknown) => {
        toast.dismiss(toastId);
        toast.error(
          exportError instanceof FilterTooLongError
            ? t('thisFilterIsTooLongToSendRemoveSome')
            : t('exportFailedPleaseTryAgain'),
        );
      })
      .finally(() => setIsExporting(false));
  }, [search, allResourceTypes, scope, t]);

  return (
    <div className="relative flex h-full flex-col gap-4">
      <PageHeader isExporting={isExporting} onExport={handleExport} />
      <GrantsKpis filteredTotal={total} />
      <PresetChips />
      <div className="flex h-0 flex-grow gap-4 overflow-hidden">
        <div ref={scrollContainerRef} className="min-w-0 flex-1 overflow-auto">
          {error instanceof FilterTooLongError ? (
            <FilterTooLongNotice queryStringBytes={error.queryStringBytes} />
          ) : status === 'error' ? (
            <SkeletonLoader status="error" type="line" />
          ) : status === 'pending' ? (
            <SkeletonLoader status="loading" type="line" />
          ) : (
            <AccessGrantsTable
              grants={grants}
              onSelect={openSlideOver}
              isLoadingMore={isFetchingNextPage}
              hasNextPage={hasNextPage ?? false}
              onLoadMore={handleLoadMore}
              scrollRoot={scrollContainerRef}
              columnVisibility={grantsView.columnVisibility}
              setColumnVisibility={grantsView.setColumnVisibility}
              activeView={grantsView.activeView}
              onSelectView={grantsView.selectView}
            />
          )}
        </div>
        <div
          className={`h-full ${
            isFilterExpanded ? 'min-w-[26rem]' : 'min-w-[1rem]'
          } rounded-panel border-container-border overflow-y-auto border bg-white transition-all duration-200 ease-out dark:bg-gray-900`}
        >
          {/* Always rendered to keep filter hook state active */}
          <div className={isFilterExpanded ? '' : 'hidden'}>
            <AccessGrantsFilters toggleExpandFilter={toggleExpandFilter} />
          </div>
          {!isFilterExpanded && (
            <div className="p-2">
              <button
                type="button"
                className="flex flex-col items-center gap-2 rounded-md p-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
                onClick={toggleExpandFilter}
                data-testid="filter-panel-toggle"
              >
                <ChevronDoubleLeftIcon className="h-4 w-4" />
                <FilterIcon />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
